import { gql, useQuery } from '@apollo/client';
import { HealthStatus } from '@provision/conversation-types';
import { useEffect } from 'react';

export interface GetDbHealthStatus {
  getHealthStatus: { status: HealthStatus };
}

export const GET_DB_HEALTH_STATUS = gql`
  query GetDbHealthStatus {
    getHealthStatus {
      status
    }
  }
`;

export const POLL_INTERVAL_MS = 60_000;

export const useDbHealthStatus = () => {
  const { data, startPolling, stopPolling, loading, error, refetch } =
    useQuery<GetDbHealthStatus>(GET_DB_HEALTH_STATUS, {
      pollInterval: POLL_INTERVAL_MS,
      notifyOnNetworkStatusChange: true,
      context: {
        ttl: 0,
      },
      fetchPolicy: 'network-only',
    });

  const status: HealthStatus | undefined = data?.getHealthStatus?.status;

  useEffect(() => {
    if (document.visibilityState !== 'visible' && status === HealthStatus.healthy) {
      stopPolling();
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling(POLL_INTERVAL_MS);
      } else {
        // only stop polling when hidden if current status is healthy
        if (status === HealthStatus.healthy) {
          stopPolling();
        }
        // if unhealthy, leave polling running so recovery is picked up
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [startPolling, stopPolling, status]);

  return {
    status,
    loading,
    error,
    refetch,
  };
};
